import * as XLSX from "xlsx";

// Canonical layout the backend (core/spec/Validate + Upload) expects. Keep this
// the single source of the template structure so the generated file can't drift.
export const COLUMN_LEVEL_HEADERS = [
  "Field #",
  "Column Name",
  "Description",
  "DataType",
  "Field Length",
  "Date Time Format Validation",
  "Text Format Validation",
  "Mandatory Column",
  "Not Null Check",
  "Case",
  "Allowable Values",
  "Row Key Field",
];

export const ALLOWABLE_HEADERS = ["Column Name", "Allowable Value", "Description"];

export const DATA_TYPES = ["string", "numeric", "date", "datetime", "boolean"];

// File Level settings the backend reads as numbers (Upload.createSpecConfigObject
// calls getNumericCellValue on these) — must be written as numeric cells, not text.
export const NUMERIC_FILE_SETTINGS = new Set([
  "Column header Row Number",
  "Number of Pre Header Rows",
  "Number of footer rows",
]);

// Build a template-matching .xlsx (browser File) from the edited model so it
// flows through the same validate → store → parse pipeline as a re-upload.
export function buildSpecWorkbook(model) {
  const wb = XLSX.utils.book_new();

  // Template User Guide — only its existence is checked.
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet([["Template User Guide"]]),
    "Template User Guide",
  );

  // File Level — setting / value / requirement / notes (vertical). Numeric
  // settings must be written as numbers so POI's getNumericCellValue succeeds.
  const flAoa = (model.fileLevel || []).map((r) => {
    const setting = (r.setting || "").trim();
    const raw = r.value ?? "";
    if (NUMERIC_FILE_SETTINGS.has(setting)) {
      const trimmed = String(raw).trim();
      const num = /^-?\d+$/.test(trimmed) ? Number(trimmed) : 0;
      return [r.setting, num, r.requirement ?? "", r.notes ?? ""];
    }
    return [r.setting, raw, r.requirement ?? "", r.notes ?? ""];
  });
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(flAoa.length ? flAoa : [[""]]),
    "File Level",
  );

  // Column Level — fixed header row + a data row per column.
  const clAoa = [COLUMN_LEVEL_HEADERS];
  (model.columnLevel?.rows || []).forEach((row, i) => {
    clAoa.push(
      COLUMN_LEVEL_HEADERS.map((h) =>
        h === "Field #" ? row["Field #"] || i + 1 : row[h] ?? "",
      ),
    );
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(clAoa), "Column Level");

  // Allowable Values.
  const avAoa = [ALLOWABLE_HEADERS];
  (model.allowableValues?.rows || []).forEach((row) =>
    avAoa.push(ALLOWABLE_HEADERS.map((h) => row[h] ?? "")),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(avAoa),
    "Allowable Values",
  );

  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new File([out], `${model.specName || "spec"}.xlsx`, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

// Mirror the server-side validation so the user gets instant feedback and the
// generated file always passes core/spec/Validate.
export function validateSpecModel(model) {
  const errors = [];
  const clRows = model.columnLevel?.rows || [];

  if (!clRows.length) errors.push("Add at least one column in Column Level.");

  const names = clRows
    .map((r) => String(r["Column Name"] || "").trim())
    .filter(Boolean);
  if (names.some((n, i) => names.indexOf(n) !== i))
    errors.push("Column names must be unique.");

  clRows.forEach((r, idx) => {
    const name = String(r["Column Name"] || "").trim();
    if (!name) {
      errors.push(`Row ${idx + 1}: Column Name is required.`);
      return;
    }
    const dt = String(r["DataType"] || "").trim().toLowerCase();
    const fl = String(r["Field Length"] || "").trim();
    if (!dt) errors.push(`${name}: DataType is required.`);
    else if (dt === "string" && !/^[1-9][0-9]*$/.test(fl))
      errors.push(`${name}: string Field Length must be a positive integer.`);
    else if (dt === "numeric" && !/^[1-9][0-9]*,[1-9][0-9]*$/.test(fl))
      errors.push(`${name}: numeric Field Length must be like 10,2.`);
    else if (dt !== "string" && dt !== "numeric" && fl)
      errors.push(`${name}: ${dt} must have a blank Field Length.`);
  });

  const avCols = new Set(
    (model.allowableValues?.rows || [])
      .map((r) => String(r["Column Name"] || "").trim())
      .filter(Boolean),
  );
  clRows.forEach((r) => {
    if (String(r["Allowable Values"] || "").trim().toUpperCase() === "Y") {
      const name = String(r["Column Name"] || "").trim();
      if (name && !avCols.has(name))
        errors.push(
          `${name}: marked Allowable Values = Y but has no rows in the Allowable Values tab.`,
        );
    }
  });

  (model.fileLevel || []).forEach((r) => {
    if (/mandatory/i.test(r.requirement || "") && !String(r.value || "").trim())
      errors.push(`File Level: "${r.setting}" is mandatory.`);
  });

  const rkv = (model.fileLevel || []).find((r) =>
    /row key validation/i.test(r.setting),
  );
  if (rkv && String(rkv.value).trim().toUpperCase() === "Y") {
    const anyRowKey = clRows.some(
      (r) => String(r["Row Key Field"] || "").trim().toUpperCase() === "Y",
    );
    if (!anyRowKey)
      errors.push(
        "Row Key Validation is Y but no column is marked Row Key Field = Y.",
      );
  }

  return errors;
}

// Normalize a parsed spec (from spec_parser) into the editable model — ensures
// every Column-Level row carries all canonical keys and a stable _key.
export function toEditableModel(parsed, specName) {
  const clRows = (parsed?.columnLevel?.rows || []).map((row, i) => {
    const out = { _key: row._key ?? i };
    COLUMN_LEVEL_HEADERS.forEach((h) => {
      out[h] = h === "Field #" ? row[h] || i + 1 : row[h] ?? "";
    });
    return out;
  });
  const avRows = (parsed?.allowableValues?.rows || []).map((row, i) => {
    const out = { _key: row._key ?? i };
    ALLOWABLE_HEADERS.forEach((h) => {
      out[h] = row[h] ?? "";
    });
    return out;
  });
  return {
    specName,
    fileLevel: (parsed?.fileLevel || []).map((r, i) => ({ ...r, _key: r._key ?? i })),
    columnLevel: { columns: COLUMN_LEVEL_HEADERS, rows: clRows },
    allowableValues: { columns: ALLOWABLE_HEADERS, rows: avRows },
  };
}
